import axiosInstance from '../../../lib/axios';
import { CASES_API_URL } from '../../../config/env';

/**
 * documentStore.js
 *
 * Centralized in-memory document buffer and blob cache for VetCollab.
 *
 * Solves:
 * 1. 401 JWT Expiration: Uses `axiosInstance` which automatically performs silent
 *    token refresh and request retry on 401.
 * 2. Range Requests & Nginx 503: Fetches the document as a clean, single ArrayBuffer
 *    request rather than issuing unpredictable HTTP Range chunks.
 * 3. Duplicate Fetches: In-flight deduplication ensures multiple components (or
 *    InlineViewer -> Modal transitions) never download the same document twice.
 * 4. Fast Rendering: Produces a memory `blob:` URL or passes `{ data: arrayBuffer }`
 *    directly to PDF.js and mammoth, yielding instantaneous 0ms renders with no CORS.
 */

// In-memory cache keyed by mediaId or URL
// Entry structure: { arrayBuffer: ArrayBuffer, blobUrl: string, mimeType: string, timestamp: number }
const cache = new Map();

// In-flight request deduplication map: key -> Promise<{ arrayBuffer, blobUrl, mimeType }>
const inFlight = new Map();

/**
 * Normalizes cache key.
 */
const getCacheKey = (mediaId, url) => {
  if (mediaId) return `media:${mediaId}`;
  if (url) return `url:${url}`;
  return null;
};

/**
 * Resolves or infers MIME type from File object or filename, preventing blank
 * content-type issues common on Windows.
 */
export const resolveMimeType = (fileOrName, explicitMime) => {
  if (explicitMime && explicitMime !== 'application/octet-stream') return explicitMime;
  if (typeof fileOrName === 'object' && fileOrName?.type && fileOrName.type !== 'application/octet-stream') {
    return fileOrName.type;
  }
  const name = (typeof fileOrName === 'string' ? fileOrName : fileOrName?.name || '').toLowerCase();
  const ext = name.split('.').pop();
  switch (ext) {
    case 'pdf':
      return 'application/pdf';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'doc':
      return 'application/msword';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    case 'mp4':
      return 'video/mp4';
    case 'mov':
      return 'video/quicktime';
    case 'webm':
      return 'video/webm';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'csv':
      return 'text/csv';
    case 'txt':
      return 'text/plain';
    case 'rtf':
      return 'application/rtf';
    case 'heic':
      return 'image/heic';
    case 'heif':
      return 'image/heif';
    default:
      return explicitMime || 'application/octet-stream';
  }
};

/**
 * Retrieves a document from cache or fetches it via axiosInstance.
 *
 * @param {Object} options
 * @param {string} [options.mediaId] - The CaseMedia DB record ID
 * @param {string} [options.url]     - Fallback / blob preview URL
 * @param {string} [options.mimeType] - MIME type hint (e.g. application/pdf)
 * @returns {Promise<{ arrayBuffer: ArrayBuffer, blobUrl: string, mimeType: string }>}
 */
export const fetchDocumentData = async ({ mediaId, url, mimeType }) => {
  const key = getCacheKey(mediaId, url);
  if (!key) {
    throw new Error('No mediaId or url provided to fetchDocumentData');
  }

  // 1. Return from memory cache if already loaded
  if (cache.has(key)) {
    return cache.get(key);
  }

  // 2. Reuse in-flight promise if a request is already running for this key
  if (inFlight.has(key)) {
    return inFlight.get(key);
  }

  const promise = (async () => {
    try {
      let arrayBuffer;
      let resolvedMime = mimeType || 'application/octet-stream';

      if (mediaId) {
        // Authenticated fetch through our backend stream proxy via axiosInstance
        // This automatically handles Bearer token injection and 401 silent refresh
        const response = await axiosInstance.get(`${CASES_API_URL}/media/stream/${mediaId}`, {
          responseType: 'arraybuffer',
        });
        arrayBuffer = response.data;
        if (response.headers?.['content-type']) {
          resolvedMime = response.headers['content-type'];
        }
      } else if (url) {
        // Local preview (blob: URL) or pre-signed URL
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`Failed to fetch document: HTTP ${res.status}`);
        }
        arrayBuffer = await res.arrayBuffer();
        if (res.headers?.get('content-type')) {
          resolvedMime = res.headers.get('content-type');
        }
      } else {
        throw new Error('Neither mediaId nor url was provided');
      }

      // Create a stable local object URL for PDF.js / download actions
      const blob = new Blob([arrayBuffer], { type: resolvedMime });
      const blobUrl = URL.createObjectURL(blob);

      const result = {
        arrayBuffer,
        blobUrl,
        mimeType: resolvedMime,
        timestamp: Date.now(),
      };

      cache.set(key, result);
      return result;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
};

/**
 * Synchronously checks if document data is already in memory.
 */
export const getCachedDocument = ({ mediaId, url }) => {
  const key = getCacheKey(mediaId, url);
  return key && cache.has(key) ? cache.get(key) : null;
};

/**
 * Evicts an entry from cache and revokes its Blob URL to reclaim memory.
 */
export const clearDocumentCache = ({ mediaId, url }) => {
  const key = getCacheKey(mediaId, url);
  if (key && cache.has(key)) {
    const entry = cache.get(key);
    if (entry.blobUrl && entry.blobUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(entry.blobUrl);
      } catch (e) {
        // Ignore revocation errors
      }
    }
    cache.delete(key);
  }
};

/**
 * Clears all cached documents and revokes blob URLs.
 */
export const clearAllDocumentCache = () => {
  for (const [, entry] of cache.entries()) {
    if (entry.blobUrl && entry.blobUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(entry.blobUrl);
      } catch (e) {
        // Ignore
      }
    }
  }
  cache.clear();
  inFlight.clear();
};
